/**
 * Portal Service (MIRA 2.0)
 */

import { apiDelete, apiGet, apiPost } from './api';
import { setPortalSession } from './portalSession';
import type {
  Actor,
  Employee,
  PortalValidationData,
  Salon,
  SalonListItem,
} from '../types/portal.types';

interface RawValidationResponse {
  isAdminMode: boolean;
  employee: { id: number; name: string } | null;
  salon: Salon | null;
  isOwner: boolean;
  actor?: Actor | null;
}

interface SessionResponse {
  sessionToken: string;
  expiresIn: number;
}

/** POST /portal/session — exchanges the start-up token for a session token (sent as X-Portal-Session). */
export async function exchangePortalToken(token: string): Promise<void> {
  const data = await apiPost<SessionResponse>('/portal/session', { body: { token } });
  setPortalSession(data.sessionToken);
}

/** POST /portal/validate-access — who the current session belongs to. */
export async function validatePortalAccess(): Promise<PortalValidationData> {
  const data = await apiPost<RawValidationResponse>('/portal/validate-access');
  return {
    isAdminMode: data.isAdminMode,
    isOwner: data.isOwner,
    actor: data.actor ?? null,
    employee: data.employee
      ? { id: data.employee.id, name: data.employee.name, isOwner: data.isOwner }
      : null,
    salon: data.salon
      ? { id: data.salon.id, tenantId: data.salon.tenantId, name: data.salon.name, provider: data.salon.provider }
      : null,
  };
}

/** GET /portal/admin/salons — salonerne i den interne brugers Mira-scope. */
export async function getAdminSalons(): Promise<SalonListItem[]> {
  const data = await apiGet<{ salons: SalonListItem[] }>('/portal/admin/salons');
  return data.salons.map((s) => ({ id: s.id, name: s.name, city: s.city ?? null }));
}

interface RawAdminMedarbejder {
  id: number;
  name: string;
  is_owner: boolean;
}

/** GET /portal/admin/salons/{salonId}/medarbejdere — salonens aktive medarbejdere. */
export async function getAdminEmployees(salonId: number): Promise<{ employees: Employee[]; salonName: string }> {
  const data = await apiGet<{ medarbejdere: RawAdminMedarbejder[]; salon_name: string }>(
    `/portal/admin/salons/${salonId}/medarbejdere`
  );
  return {
    employees: data.medarbejdere.map((e) => ({ id: e.id, name: e.name, isOwner: e.is_owner })),
    salonName: data.salon_name,
  };
}

/** POST /portal/admin/impersonation — portalen kører herefter som medarbejderen. */
export async function startImpersonation(medarbejderId: number): Promise<void> {
  await apiPost('/portal/admin/impersonation', { body: { medarbejder_id: medarbejderId } });
}

/** DELETE /portal/admin/impersonation — tilbage til admin-tilstand. */
export async function stopImpersonation(): Promise<void> {
  await apiDelete('/portal/admin/impersonation');
}

export type { PortalValidationData, Salon };
